// /home/labdoo/zitech-nexus/frontend/src/pages/admin/portfolio/AdminSolutionForm.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { portfolioAPI } from '../../../services/api';
import './PortfolioAdmin.css';

// SVG Icons
const Icons = {
  Back: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 12H5M12 19l-7-7 7-7" />
    </svg>
  ),
  Save: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
      <polyline points="17 21 17 13 7 13 7 21" />
      <polyline points="7 3 7 8 15 8" />
    </svg>
  ),
  Cancel: () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Plus: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <path d="M12 5V19M5 12H19" />
    </svg>
  ),
  X: () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
  Spinner: () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pa-spinner-small">
      <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
    </svg>
  ),
  Icon: () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2L2 7l10 5 10-5-10-5z" />
      <path d="M2 17l10 5 10-5" />
      <path d="M2 12l10 5 10-5" />
    </svg>
  ),
};

interface SolutionFormData {
  title: string;
  description: string;
  icon_name: string;
  benefits: string[];
  technologies: string[];
  detailed_description: string;
  use_cases: string[];
  is_featured: boolean;
  is_published: boolean;
  order: number;
  meta_title: string;
  meta_description: string;
}

const AdminSolutionForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [error, setError] = useState('');
  const [benefitInput, setBenefitInput] = useState('');
  const [techInput, setTechInput] = useState('');
  const [useCaseInput, setUseCaseInput] = useState('');

  const [formData, setFormData] = useState<SolutionFormData>({
    title: '',
    description: '',
    icon_name: 'digital_transformation',
    benefits: [],
    technologies: [],
    detailed_description: '',
    use_cases: [],
    is_featured: false,
    is_published: true,
    order: 0,
    meta_title: '',
    meta_description: '',
  });

  const iconOptions = [
    { value: 'digital_transformation', label: 'Digital Transformation' },
    { value: 'cloud_migration', label: 'Cloud Migration' },
    { value: 'cybersecurity', label: 'Cybersecurity' },
    { value: 'data_analytics', label: 'Data Analytics' },
    { value: 'devops', label: 'DevOps Automation' },
    { value: 'ai_ml', label: 'AI & Machine Learning' },
  ];

  useEffect(() => {
    if (id) {
      fetchSolution();
    } else {
      setFetching(false);
    }
  }, [id]);

  const fetchSolution = async () => {
    try {
      setFetching(true);
      setError('');
      const response = await portfolioAPI.adminGetSolution(id!);
      
      const solution = response.data;
      setFormData({
        title: solution.title,
        description: solution.description,
        icon_name: solution.icon_name,
        benefits: solution.benefits || [],
        technologies: solution.technologies || [],
        detailed_description: solution.detailed_description || '',
        use_cases: solution.use_cases || [],
        is_featured: solution.is_featured,
        is_published: solution.is_published,
        order: solution.order || 0,
        meta_title: solution.meta_title || '',
        meta_description: solution.meta_description || '',
      });
    } catch (err: any) {
      console.error('Error fetching solution:', err);
      setError('Failed to load solution');
    } finally {
      setFetching(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
    if (error) setError('');
  };

  const handleAddBenefit = () => {
    if (benefitInput.trim() && !formData.benefits.includes(benefitInput.trim())) {
      setFormData(prev => ({
        ...prev,
        benefits: [...prev.benefits, benefitInput.trim()]
      }));
      setBenefitInput('');
    }
  };

  const handleRemoveBenefit = (benefit: string) => {
    setFormData(prev => ({
      ...prev,
      benefits: prev.benefits.filter(b => b !== benefit)
    }));
  };

  const handleAddTech = () => {
    if (techInput.trim() && !formData.technologies.includes(techInput.trim())) {
      setFormData(prev => ({
        ...prev,
        technologies: [...prev.technologies, techInput.trim()]
      }));
      setTechInput('');
    }
  };

  const handleRemoveTech = (tech: string) => {
    setFormData(prev => ({
      ...prev,
      technologies: prev.technologies.filter(t => t !== tech)
    }));
  };

  const handleAddUseCase = () => {
    if (useCaseInput.trim() && !formData.use_cases.includes(useCaseInput.trim())) {
      setFormData(prev => ({
        ...prev,
        use_cases: [...prev.use_cases, useCaseInput.trim()]
      }));
      setUseCaseInput('');
    }
  };

  const handleRemoveUseCase = (useCase: string) => {
    setFormData(prev => ({
      ...prev,
      use_cases: prev.use_cases.filter(u => u !== useCase)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (id) {
        const submitData = {
          title: formData.title,
          description: formData.description,
          icon_name: formData.icon_name,
          benefits: formData.benefits,
          technologies: formData.technologies,
          detailed_description: formData.detailed_description,
          use_cases: formData.use_cases,
          is_featured: formData.is_featured,
          is_published: formData.is_published,
          order: formData.order,
          meta_title: formData.meta_title || '',
          meta_description: formData.meta_description || '',
        };

        await portfolioAPI.adminUpdateSolution(id, submitData);
        alert('Solution updated successfully!');
      } else {
        const submitData = new FormData();
        submitData.append('title', formData.title);
        submitData.append('description', formData.description);
        submitData.append('icon_name', formData.icon_name);
        submitData.append('benefits', JSON.stringify(formData.benefits));
        submitData.append('technologies', JSON.stringify(formData.technologies));
        submitData.append('detailed_description', formData.detailed_description);
        submitData.append('use_cases', JSON.stringify(formData.use_cases));
        submitData.append('is_featured', String(formData.is_featured));
        submitData.append('is_published', String(formData.is_published));
        submitData.append('order', String(formData.order));
        submitData.append('meta_title', formData.meta_title || '');
        submitData.append('meta_description', formData.meta_description || '');

        await portfolioAPI.adminCreateSolution(submitData);
        alert('Solution created successfully!');
      }

      navigate('/dashboard/admin/portfolio/solutions');
    } catch (err: any) {
      console.error('Error saving solution:', err);
      setError('Failed to save solution');
    } finally {
      setLoading(false);
    }
  };

  if (fetching) {
    return (
      <div className="pa-loading-container">
        <div className="pa-spinner">
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#0d47a1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="pa-spinner-svg">
            <path d="M12 2v4M12 22v-4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M22 12h-4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
          </svg>
        </div>
        <p className="pa-loading-text">Loading solution...</p>
      </div>
    );
  }

  if (error && !formData.title) {
    return (
      <div className="pa-error-container">
        <div className="pa-error-icon">⚠️</div>
        <h2 className="pa-error-title">Error Loading Solution</h2>
        <p className="pa-error-text">{error}</p>
        <button className="pa-btn-secondary" onClick={() => navigate('/dashboard/admin/portfolio/solutions')}>
          <Icons.Back />
          Back to Solutions
        </button>
      </div>
    );
  }

  return (
    <div className="pa-form-container">
      {/* Header */}
      <div className="pa-form-header">
        <div className="pa-form-header-left">
          <button className="pa-back-btn" onClick={() => navigate('/dashboard/admin/portfolio/solutions')}>
            <Icons.Back />
            Back to Solutions
          </button>
          <h1 className="pa-form-title">{id ? 'Edit Solution' : 'Create New Solution'}</h1>
        </div>
        <button className="pa-btn-secondary" onClick={() => navigate('/dashboard/admin/portfolio/solutions')}>
          <Icons.Cancel />
          Cancel
        </button>
      </div>

      {error && <div className="pa-error">{error}</div>}

      {/* Form */}
      <form onSubmit={handleSubmit} className="pa-form">
        <div className="pa-form-grid">
          {/* Left Column */}
          <div className="pa-form-left">
            <div className="pa-card">
              <h3 className="pa-card-title">
                <Icons.Icon />
                Basic Information
              </h3>
              
              <div className="pa-form-row">
                <div className="pa-form-group">
                  <label className="pa-form-label">Title *</label>
                  <input
                    type="text"
                    name="title"
                    className="pa-form-input"
                    value={formData.title}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="pa-form-group">
                  <label className="pa-form-label">Icon *</label>
                  <select name="icon_name" className="pa-form-select" value={formData.icon_name} onChange={handleChange} required>
                    {iconOptions.map(icon => (
                      <option key={icon.value} value={icon.value}>{icon.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">Short Description *</label>
                <textarea
                  name="description"
                  className="pa-form-textarea"
                  value={formData.description}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">Detailed Description</label>
                <textarea
                  name="detailed_description"
                  className="pa-form-textarea"
                  value={formData.detailed_description}
                  onChange={handleChange}
                  rows={6}
                />
              </div>
            </div>

            <div className="pa-card">
              <h3 className="pa-card-title">SEO</h3>
              
              <div className="pa-form-group">
                <label className="pa-form-label">Meta Title</label>
                <input
                  type="text"
                  name="meta_title"
                  className="pa-form-input"
                  value={formData.meta_title}
                  onChange={handleChange}
                />
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">Meta Description</label>
                <textarea
                  name="meta_description"
                  className="pa-form-textarea"
                  value={formData.meta_description}
                  onChange={handleChange}
                  rows={3}
                />
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="pa-form-right">
            <div className="pa-card">
              <h3 className="pa-card-title">Benefits</h3>
              <div className="pa-tag-group">
                <div className="pa-tag-input">
                  <input
                    type="text"
                    value={benefitInput}
                    onChange={(e) => setBenefitInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddBenefit()}
                    placeholder="Type benefit and press Enter"
                  />
                  <button type="button" onClick={handleAddBenefit}>
                    <Icons.Plus />
                  </button>
                </div>
                <div className="pa-tags">
                  {formData.benefits.map((benefit, idx) => (
                    <span key={idx} className="pa-tag">
                      {benefit}
                      <button type="button" onClick={() => handleRemoveBenefit(benefit)}>
                        <Icons.X />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="pa-card">
              <h3 className="pa-card-title">Technologies</h3>
              <div className="pa-tag-group">
                <div className="pa-tag-input">
                  <input
                    type="text"
                    value={techInput}
                    onChange={(e) => setTechInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddTech()}
                    placeholder="Type technology and press Enter"
                  />
                  <button type="button" onClick={handleAddTech}>
                    <Icons.Plus />
                  </button>
                </div>
                <div className="pa-tags">
                  {formData.technologies.map((tech, idx) => (
                    <span key={idx} className="pa-tag">
                      {tech}
                      <button type="button" onClick={() => handleRemoveTech(tech)}>
                        <Icons.X />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="pa-card">
              <h3 className="pa-card-title">Use Cases</h3>
              <div className="pa-tag-group">
                <div className="pa-tag-input">
                  <input
                    type="text"
                    value={useCaseInput}
                    onChange={(e) => setUseCaseInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleAddUseCase()}
                    placeholder="Type use case and press Enter"
                  />
                  <button type="button" onClick={handleAddUseCase}>
                    <Icons.Plus />
                  </button>
                </div>
                <div className="pa-tags">
                  {formData.use_cases.map((useCase, idx) => (
                    <span key={idx} className="pa-tag">
                      {useCase}
                      <button type="button" onClick={() => handleRemoveUseCase(useCase)}>
                        <Icons.X />
                      </button>
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="pa-card">
              <h3 className="pa-card-title">Settings</h3>
              
              <div className="pa-form-group pa-form-checkbox">
                <label className="pa-checkbox-label">
                  <input
                    type="checkbox"
                    name="is_featured"
                    checked={formData.is_featured}
                    onChange={handleChange}
                  />
                  <span className="pa-checkbox-text">Featured Solution</span>
                </label>
              </div>

              <div className="pa-form-group pa-form-checkbox">
                <label className="pa-checkbox-label">
                  <input
                    type="checkbox"
                    name="is_published"
                    checked={formData.is_published}
                    onChange={handleChange}
                  />
                  <span className="pa-checkbox-text">Publish</span>
                </label>
                <span className="pa-form-hint">Unpublished solutions won't appear on the public website</span>
              </div>

              <div className="pa-form-group">
                <label className="pa-form-label">Display Order</label>
                <input
                  type="number"
                  name="order"
                  className="pa-form-input"
                  value={formData.order}
                  onChange={handleChange}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="pa-form-actions">
          <button 
            type="button" 
            className="pa-btn-secondary" 
            onClick={() => navigate('/dashboard/admin/portfolio/solutions')}
          >
            Cancel
          </button>
          <button type="submit" className="pa-btn-primary" disabled={loading}>
            {loading ? (
              <>
                <Icons.Spinner />
                Saving...
              </>
            ) : (
              <>
                <Icons.Save />
                {id ? 'Update Solution' : 'Create Solution'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminSolutionForm;